import React, { useEffect } from 'react'
import { Box, Button } from '@mui/material'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  crossAssociationTreeAtom,
  globalMessageAtom,
  subTreeAtom,
  subTreeNodeIdMappingAtom,
} from './atoms'
import {
  useCreateCrossAssociationRoot,
  useLangAttributeParams,
  useRemoveCrossAssociationRoot,
  useSubtreeSummary,
} from './hooks'
import { Table } from 'shared/ui/Table/Table'
import { Column } from 'shared/ui/Table/types'
import { nodeTypesNeedingLinks, XRefRootKey } from './utility'
import { SubTreeInput } from './SubTreeInput'
import { LanguageSelect, VanityIdSelect } from './components'
import { useGetTreeById } from '../../../reducers/trees_hooks'
import { normalizeTreeForJsonView } from '../TreeEditingV2/utility'
import { MatchedTable } from './MatchedTable'
import { NodeLanguageAttribute } from '../LanguageAttribute/NodeLanguageAttribute'

export function NodeCrossAssociation(ids: {
  treeId: string
  versionId: string
}) {
  const { tree } = useGetTreeById(ids)
  const setTree = useSetAtom(crossAssociationTreeAtom)
  const message = useAtomValue(globalMessageAtom)

  useEffect(() => {
    setTree(tree && normalizeTreeForJsonView(tree))
  }, [setTree, tree])

  return (
    <Box>
      {message && <Box padding="20px">{message}</Box>}
      <SubTreeInput />
      <VanityIdSelect />
      <SelectedSubtrees />
      <LanguageAttribute />
      <Box padding="10px">
        <MatchedTable />
      </Box>
      <Box padding="10px">
        <MatchSummary />
      </Box>
    </Box>
  )
}

function LanguageAttribute() {
  const params = useLangAttributeParams()
  if (!params) return null
  const { treeId, versionId, branches } = params
  return (
    <NodeLanguageAttribute
      treeId={treeId}
      versionId={versionId}
      branches={branches}
    />
  )
}

function SelectedSubtrees() {
  const subtrees = useAtomValue(subTreeAtom)
  const removeCrossAssociation = useRemoveCrossAssociationRoot()
  const createCrossAssociation = useCreateCrossAssociationRoot()

  if (!subtrees.length) return null

  return (
    <Box padding="10px">
      {subtrees.map((subTree, index) => {
        const { id, type, title, body } = subTree
        return (
          <Box key={id}>
            <Box>
              {`Subtree ${index + 1}: ${type}: ${title}`}
              <LanguageSelect
                selectedLanguage={body[XRefRootKey].language}
                onLanguageSelect={(value) =>
                  createCrossAssociation({ nodeId: id, language: value })
                }
              />
              <Button onClick={() => removeCrossAssociation(id)}>X</Button>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

function MatchSummary() {
  const summary = useSubtreeSummary()
  const matches = useAtomValue(subTreeNodeIdMappingAtom)
  if (!summary) return null
  const minCount = Math.min(...summary.map((data) => data.total))
  const tableTitle =
    minCount === matches.length
      ? `All ${minCount} Matched`
      : `${minCount - matches.length} UnMatched`

  const columns: Column<Record<string, number | string>>[] = [
    {
      title: 'Title',
      rowContent: ({ title }) => {
        return title
      },
    },
    {
      title: 'Type',
      rowContent: ({ type }) => {
        return type
      },
    },
  ]

  nodeTypesNeedingLinks.forEach((type) => {
    columns.push({
      title: type,
      rowContent: ({ [type]: typeCount }) => {
        return typeCount
      },
    })
  })

  columns.push({
    title: 'Total',
    rowContent: ({ total }) => {
      return total
    },
  })
  return (
    <Table
      overrideHeight={200}
      title={tableTitle}
      columns={columns}
      items={summary}
    />
  )
}
